<template>
  <section class="request-detail-basic-details" data-testid="request-detail-basic-details">
    <div class="request-detail-basic-details__card">
      <p class="request-detail-basic-details__heading">Details</p>

      <div class="request-detail-basic-details__grid">
        <div class="request-detail-basic-details__field">
          <span class="request-detail-basic-details__label">Requested from</span>
          <div
            class="request-detail-basic-details__person"
            data-testid="request-detail-basic-details__requested-from"
          >
            <span class="request-detail-basic-details__person-name">{{ ownerName }}</span>
            <span
              v-if="ownerEmail"
              class="request-detail-basic-details__person-email"
            >{{ ownerEmail }}</span>
          </div>
        </div>

        <div class="request-detail-basic-details__field">
          <span class="request-detail-basic-details__label">Requested by</span>
          <div
            class="request-detail-basic-details__person"
            data-testid="request-detail-basic-details__requested-by"
          >
            <span class="request-detail-basic-details__person-name">{{ requestedByName }}</span>
            <span
              v-if="requestedByEmail"
              class="request-detail-basic-details__person-email"
            >{{ requestedByEmail }}</span>
          </div>
        </div>

        <div class="request-detail-basic-details__field">
          <span class="request-detail-basic-details__label">Classification</span>
          <span
            class="request-detail-basic-details__value"
            data-testid="request-detail-basic-details__classification"
          >
            {{ request.category || '—' }}
          </span>
        </div>

        <div class="request-detail-basic-details__field">
          <span class="request-detail-basic-details__label">Priority</span>
          <span class="request-detail-basic-details__value">
            <TaskPriorityDisplay
              v-if="request.priority"
              :priority="request.priority"
              data-test-id="request-detail-basic-details__priority"
            />
            <template v-else>—</template>
          </span>
        </div>

        <div class="request-detail-basic-details__field">
          <span class="request-detail-basic-details__label">Response type</span>
          <span
            class="request-detail-basic-details__value request-detail-basic-details__value--strong"
            data-testid="request-detail-basic-details__response-type"
          >
            {{ responseTypeLabel }}
          </span>
        </div>

        <div class="request-detail-basic-details__field">
          <span class="request-detail-basic-details__label">{{ locationHeader }}</span>
          <span
            class="request-detail-basic-details__value request-detail-basic-details__value--strong"
            data-testid="request-detail-basic-details__location"
          >
            {{ locationName || '—' }}
          </span>
        </div>

        <div
          v-if="linkedTasks.length"
          class="request-detail-basic-details__field request-detail-basic-details__field--full"
        >
          <span class="request-detail-basic-details__label">Linked Task</span>
          <div class="request-detail-basic-details__chips">
            <v-chip
              v-for="task in linkedTasks"
              :key="task._id || task.ref || task.master_id"
              class="request-detail-basic-details__chip"
              :data-testid="`request-detail-basic-details__linked-task-${task._id || task.ref}`"
            >
              <span class="request-detail-basic-details__chip-text">
                {{ formatLinkedTask(task) }}
              </span>
            </v-chip>
          </div>
        </div>

        <div
          v-if="request.instruction"
          class="request-detail-basic-details__field request-detail-basic-details__field--full"
        >
          <span class="request-detail-basic-details__label">Instructions</span>
          <span
            class="request-detail-basic-details__value"
            data-testid="request-detail-basic-details__instruction"
          >
            {{ request.instruction }}
          </span>
        </div>

        <div
          v-if="accrualsDescriptionView.hasContent"
          class="request-detail-basic-details__field request-detail-basic-details__field--full"
          data-testid="request-detail-basic-details__accruals-description"
        >
          <span class="request-detail-basic-details__label">Accruals description</span>

          <div
            v-if="accrualsDescriptionView.poFields.length"
            class="request-detail-basic-details__accruals-section"
            data-testid="request-detail-basic-details__accruals-po"
          >
            <p class="request-detail-basic-details__accruals-heading">Purchase order</p>
            <div class="request-detail-basic-details__grid request-detail-basic-details__grid--nested">
              <div
                v-for="field in accrualsDescriptionView.poFields"
                :key="`po-${field.key}`"
                class="request-detail-basic-details__field"
              >
                <span class="request-detail-basic-details__label">{{ field.label }}</span>
                <span class="request-detail-basic-details__value request-detail-basic-details__value--accrual">
                  {{ field.value }}
                </span>
              </div>
            </div>
          </div>

          <div
            v-if="accrualsDescriptionView.vendorFields.length"
            class="request-detail-basic-details__accruals-section"
            data-testid="request-detail-basic-details__accruals-vendor"
          >
            <p class="request-detail-basic-details__accruals-heading">Vendor</p>
            <div class="request-detail-basic-details__grid request-detail-basic-details__grid--nested">
              <div
                v-for="field in accrualsDescriptionView.vendorFields"
                :key="`vendor-${field.key}`"
                class="request-detail-basic-details__field"
              >
                <span class="request-detail-basic-details__label">{{ field.label }}</span>
                <span class="request-detail-basic-details__value request-detail-basic-details__value--accrual">
                  {{ field.value }}
                </span>
              </div>
            </div>
          </div>

          <div
            v-if="accrualsDescriptionView.apRows.length"
            class="request-detail-basic-details__accruals-section"
            data-testid="request-detail-basic-details__accruals-ap"
          >
            <p class="request-detail-basic-details__accruals-heading">AP information</p>
            <div
              v-for="(row, rowIndex) in accrualsDescriptionView.apRows"
              :key="row.key"
              class="request-detail-basic-details__accruals-row"
              :data-testid="`request-detail-basic-details__accruals-ap-row--${rowIndex}`"
            >
              <p
                v-if="accrualsDescriptionView.apRows.length > 1"
                class="request-detail-basic-details__accruals-row-label"
              >
                AP {{ rowIndex + 1 }}
              </p>
              <div class="request-detail-basic-details__grid request-detail-basic-details__grid--nested">
                <div
                  v-for="field in row.fields"
                  :key="`ap-${row.key}-${field.key}`"
                  class="request-detail-basic-details__field"
                >
                  <span class="request-detail-basic-details__label">{{ field.label }}</span>
                  <span class="request-detail-basic-details__value request-detail-basic-details__value--accrual">
                    {{ field.value }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="accrualsDescriptionView.openInvoiceRows.length"
            class="request-detail-basic-details__accruals-section"
            data-testid="request-detail-basic-details__accruals-open-invoices"
          >
            <p class="request-detail-basic-details__accruals-heading">Unapproved invoices</p>
            <div
              v-for="(row, rowIndex) in accrualsDescriptionView.openInvoiceRows"
              :key="row.key"
              class="request-detail-basic-details__accruals-row"
              :data-testid="`request-detail-basic-details__accruals-open-invoice-row--${rowIndex}`"
            >
              <p
                v-if="accrualsDescriptionView.openInvoiceRows.length > 1"
                class="request-detail-basic-details__accruals-row-label"
              >
                Invoice {{ rowIndex + 1 }}
              </p>
              <div class="request-detail-basic-details__grid request-detail-basic-details__grid--nested">
                <div
                  v-for="field in row.fields"
                  :key="`oi-${row.key}-${field.key}`"
                  class="request-detail-basic-details__field"
                >
                  <span class="request-detail-basic-details__label">{{ field.label }}</span>
                  <span class="request-detail-basic-details__value request-detail-basic-details__value--accrual">
                    {{ field.value }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import './RequestDetailBasicDetails.scss';
import { TaskPriorityDisplay } from '../../../TaskList/components';
import {
  buildAccrualsDescriptionView,
  resolveResponseTypeLabel,
} from '../../utils/requestDetail';

export default {
  name: 'RequestDetailBasicDetails',
  components: {
    TaskPriorityDisplay,
  },
  props: {
    request: {
      type: Object,
      required: true,
    },
    locationName: {
      type: String,
      default: '',
    },
    locationHeader: {
      type: String,
      default: 'Location',
    },
  },
  computed: {
    ownerDetail() {
      return this.request?.info_owner_detail || {};
    },
    ownerName() {
      return this.ownerDetail.name || this.ownerDetail.email || '—';
    },
    ownerEmail() {
      return this.ownerDetail.name && this.ownerDetail.email ? this.ownerDetail.email : '';
    },
    requestedByDetail() {
      return this.request?.created_by_detail || {};
    },
    requestedByName() {
      return this.requestedByDetail.name
        || this.request?.created_by_name
        || this.requestedByDetail.email
        || this.request?.created_by_email
        || '—';
    },
    requestedByEmail() {
      const name = this.requestedByDetail.name || this.request?.created_by_name;
      const email = this.requestedByDetail.email || this.request?.created_by_email;
      return name && email ? email : '';
    },
    responseTypeLabel() {
      return resolveResponseTypeLabel(this.request?.type);
    },
    linkedTasks() {
      return Array.isArray(this.request?.referenceChecklist) ? this.request.referenceChecklist : [];
    },
    accrualsDescriptionView() {
      return buildAccrualsDescriptionView(this.request?.accruals_description);
    },
  },
  methods: {
    truncateTaskName(name, maxLength = 22) {
      if (!name) return '';
      return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
    },
    formatLinkedTask(task) {
      const serial = task.ref || task.master_id || '';
      const name = this.truncateTaskName(task.description || task.name || '');
      if (serial && name) return `${serial} · ${name}`;
      return serial || name || 'Linked task';
    },
  },
};
</script>
